import React, { useState } from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';
import { CheckRounded, ContentCopy, ExpandMore } from '@mui/icons-material';

/**
 * Sticky panel showing the serialized template JSON with copy control. Collapsible, since most
 * users never need the raw data.
 * @param {object} props - Props.
 * @param {string} props.generatedJson - JSON string.
 * @param {boolean} props.isCopied - Copy feedback flag.
 * @param {function(): void} props.onCopy - Copy handler.
 * @returns {React.ReactElement}
 */
function PowerPointJsonPanel({ generatedJson, isCopied, onCopy }) {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        position: { lg: 'sticky' },
        top: { lg: 'calc(var(--q-page-sticky-top, 0px) + 80px)' },
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 'var(--q-radius-card)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600 }}>Template data</Typography>
          <Typography variant="caption" color="text.secondary">The JSON saved for this deck</Typography>
        </Box>
        <Button
          size="small"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="ppt-template-json"
          endIcon={<ExpandMore sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>
      <Collapse in={open}>
        <Box id="ppt-template-json" sx={{ borderTop: '1px solid var(--q-border-subtle)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'var(--q-font-mono)', fontSize: 12.5 }}>
              template.json
            </Typography>
            <Button
              onClick={onCopy}
              size="small"
              variant="outlined"
              startIcon={isCopied ? <CheckRounded /> : <ContentCopy />}
              aria-live="polite"
            >
              {isCopied ? 'Copied' : 'Copy'}
            </Button>
          </Box>
          <Box
            component="pre"
            sx={{
              m: 0,
              px: 2,
              py: 1.5,
              maxHeight: '60vh',
              overflow: 'auto',
              bgcolor: 'var(--q-subtle)',
              color: 'var(--q-text-2)',
              fontFamily: 'var(--q-font-mono)',
              fontSize: 12,
              lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <code>{generatedJson}</code>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default PowerPointJsonPanel;
